import { Ban, CheckCircle2, RotateCcw, Tags } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { RoutingDocumentTypeModal } from '../components/RoutingDocumentTypeModal';
import { routingDocumentTypesApi } from '../routingApi';
import { ROUTING_SETTINGS_PATH } from '../routingRules';
import type { RoutingDocumentTypeListItem } from '../routingTypes';

type TabKey = 'info';

const BREADCRUMB: BreadcrumbSegment[] = [
  'Document Management',
  'Routing',
  { label: 'Settings', to: ROUTING_SETTINGS_PATH },
  { label: 'Document Types', to: `${ROUTING_SETTINGS_PATH}/document-types` },
  'Document Type',
];

/**
 * One routing document type. Thin, and that is the point: what it carries that the form cannot is
 * how many documents are filed under it — what a reader wants before renaming or deleting it.
 */
export function RoutingDocumentTypeProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [type, setType] = useState<RoutingDocumentTypeListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      routingDocumentTypesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setType(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this document type.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** An undo — no tick and no password. The server still refuses a name taken meanwhile. */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await routingDocumentTypesApi.restore(id);
      toast.success('Restored the document type.');
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this document type.', 0));
    } finally {
      setRestoring(false);
    }
  }

  if (error && !type) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !type) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={BREADCRUMB} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (!type) return null;

  const filed = type.receivedCount + type.dispatchedCount;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={BREADCRUMB} />

      <ProfileHeaderShell
        id="routing-document-type-profile"
        header={
          <ProfileHeader
            id="routing-document-type-profile-header"
            icon={<Tags className="h-6 w-6" aria-hidden="true" />}
            title={type.name}
            meta={[`${filed} ${filed === 1 ? 'document is' : 'documents are'} filed under it`]}
            status={<StatusText tone={type.isActive ? 'active' : 'inactive'}>{type.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={type.createdByName}
            createdAt={type.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="routing-document-type-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : type.isActive ? (
            <ProfileBanner
              id="routing-document-type-profile-banner"
              tone="slate"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This document type is offered on the Receiving and Dispatching forms."
            />
          ) : (
            <ProfileBanner
              id="routing-document-type-profile-banner"
              tone="red"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This document type is deleted. It is no longer offered, and the documents already filed under it keep naming it."
              action={
                <PermissionGate permission="routing-document-types.delete">
                  <Button
                    id="routing-document-type-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="routing-document-type-profile-details"
          title="Details"
          onEdit={() => setEditing(true)}
          editPermission="routing-document-types.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: type.name },
              { label: 'Description', value: type.description },
              {
                label: 'Received documents filed under it',
                value: (
                  <span className={`font-mono tabular-nums ${type.receivedCount === 0 ? 'text-slate-300' : ''}`}>
                    {type.receivedCount}
                  </span>
                ),
              },
              {
                label: 'Dispatched documents filed under it',
                value: (
                  <span className={`font-mono tabular-nums ${type.dispatchedCount === 0 ? 'text-slate-300' : ''}`}>
                    {type.dispatchedCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="routing-document-type-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: type.createdByName },
              { label: 'Created', value: formatDateTime(type.createdAt), mono: true },
              { label: 'Updated by', value: type.updatedByName },
              { label: 'Updated', value: type.updatedAt ? formatDateTime(type.updatedAt) : null, mono: true },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <RoutingDocumentTypeModal
          open
          editing={type}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
